/**
 * Add / edit a client.
 *
 * The form is generated from FIELDS, so it never drifts from the table or the
 * CSV export. The amber strip underneath restates, in plain words, the ways
 * this record would misbehave once saved — a trial with no end date, a paid
 * plan with no billing cycle, and so on.
 */
import { useState, useMemo } from 'react';
import { FIELDS, headingOf } from '../../constants/defaults.js';
import { useApp } from '../../store/AppContext.jsx';
import { parseDate, formatDate, addDays, addPeriod, periodDays } from '../../lib/dates.js';
import { lower } from '../../lib/format.js';
import { priceOf, planAmount, modulePrice, moduleList } from '../../domain/pricing.js';
import { inferType } from '../../domain/derive.js';
import { isOffline } from '../../lib/storage.js';
import Sheet, { Group, Field, QuickActions, Warning } from '../Sheet.jsx';
import ClientStatement from './ClientStatement.jsx';

const NO_TRIAL = 'No trial';
const EDITABLE = FIELDS.filter(f => f.t !== 'derived');

export default function ClientSheet({ id, onClose }) {
  const {
    S, rows, derive, saveClient, markPaid, openDraft, nextSno, patchSettings, toast, fmtMoney
  } = useApp();

  const existing = id ? rows.find(r => r.id === id) : null;

  /* Seed the form from the record, or from sensible defaults for a new one. */
  const [form, setForm] = useState(() => {
    const base = {};
    EDITABLE.forEach(f => {
      base[f.k] = existing?.[f.k] != null ? String(existing[f.k]) : (f.k === 'sno' ? String(nextSno()) : '');
    });
    if (existing && !base.acctType) base.acctType = derive(existing).type;
    return base;
  });

  const set = (k, v) => setForm(f => ({ ...f, [k]: v }));

  const selectedModules = useMemo(
    () => String(form.addons || '').split(';').map(s => s.trim()).filter(Boolean),
    [form.addons]
  );

  const toggleModule = name => {
    const next = selectedModules.includes(name)
      ? selectedModules.filter(m => m !== name)
      : [...selectedModules, name];
    set('addons', next.join('; '));
  };

  const bundled = (S.bundlePlans || []).includes(String(form.plan).toUpperCase());
  const addonExtra = bundled ? 0 : selectedModules.reduce((t, m) => t + modulePrice(S, m), 0);

  /* ---- advisory warnings, recomputed as the form changes ---- */
  const warnings = useMemo(() => {
    const msgs = [];
    const plan = String(form.plan).toUpperCase();
    const trial = String(form.trialEnd || '').trim();
    const paidPlan = priceOf(S, form.plan) > 0;
    const cycleDays = periodDays(form.cycle);
    const amount = parseFloat(form.amount);

    if ((form.acctType === 'Trial' || plan === 'TRIAL') && !trial) {
      msgs.push('Trial account with no trial end date — set one, or mark it as no trial.');
    }
    if (!String(form.handler || '').trim()) {
      msgs.push('No owner assigned, so nobody will chase this account.');
    }
    if (form.acctType === 'Paying' && !lower(S.paidValues).includes(String(form.pay).toLowerCase())) {
      msgs.push('Marked as paying, but the payment status is not a paid value.');
    }
    if (paidPlan && !cycleDays) {
      msgs.push('Pick Monthly or Annual — a paid plan needs a billing cycle before it can recur.');
    }
    if (paidPlan && cycleDays && !amount) {
      msgs.push(`Set the amount charged each ${String(form.cycle).toLowerCase() === 'annual' ? 'year' : 'period'}.`);
    }
    if (paidPlan && cycleDays && amount && !String(form.nextDue || '').trim()) {
      msgs.push('No next due date — the recurring schedule will start from the trial end or registration.');
    }
    return msgs;
  }, [form, S]);

  /* ---- quick actions ---- */
  const setTrialFromRegistration = () => {
    const reg = parseDate(form.regOn);
    if (!reg) { toast('Add a registration date first'); return; }
    set('trialEnd', formatDate(addDays(reg, S.trialDays), false, S.dateFmt));
  };

  const usePlanPrice = () => {
    const v = planAmount(S, { plan: form.plan, cycle: form.cycle, addons: form.addons });
    if (!v) { toast('That plan has no price set'); return; }
    set('amount', String(v));
  };

  /** A "list" field offers an add-new option that extends the settings list. */
  const addNewValue = (fieldKey, listKey) => {
    // eslint-disable-next-line no-alert
    const v = (window.prompt(`Name the new ${listKey === 'owners' ? 'owner' : 'source'}:`) || '').trim();
    if (!v) return;
    if (!S[listKey].includes(v)) patchSettings({ [listKey]: [...S[listKey], v] });
    set(fieldKey, v);
  };

  const save = () => {
    const data = {};
    EDITABLE.forEach(f => { data[f.k] = String(form[f.k] ?? '').trim(); });
    if (!data.name) { toast('A client name is required'); return; }
    if (!data.acctType) data.acctType = inferType(S, data);
    saveClient(id, data);
    onClose();
  };

  /* ---- one form control per field ---- */
  const renderInput = f => {
    const value = form[f.k] ?? '';

    if (f.t === 'opt' || f.t === 'list') {
      const base = (f.src ? S[f.src] : []) || [];
      const used = [...new Set(rows.map(r => String(r[f.k] || '').trim()).filter(Boolean))];
      const options = [...new Set(['', ...base, ...used, value ? String(value) : ''])].filter((v, i, a) => a.indexOf(v) === i);

      return (
        <select
          value={value}
          onChange={e => {
            if (e.target.value === '__new') {
              addNewValue(f.k, f.k === 'handler' ? 'owners' : 'sources');
              return;
            }
            set(f.k, e.target.value);
          }}
        >
          {options.map(o => <option key={o || '_blank'} value={o}>{o || '—'}</option>)}
          {f.t === 'list' ? <option value="__new">＋ Add new…</option> : null}
        </select>
      );
    }

    if (f.t === 'mods') {
      return (
        <>
          <div className="checks">
            {Object.keys(S.modules || {}).map(m => (
              <label key={m}>
                <input
                  type="checkbox"
                  checked={selectedModules.includes(m)}
                  onChange={() => toggleModule(m)}
                />
                {m}
                {modulePrice(S, m) ? <span className="dim"> {fmtMoney(modulePrice(S, m))}/mo</span> : null}
              </label>
            ))}
          </div>
          <div style={{ fontSize: 11.5, color: 'var(--ink2)' }}>
            {bundled
              ? 'This plan bundles every add-on, so nothing extra is charged.'
              : selectedModules.length
                ? `${selectedModules.length} add-on${selectedModules.length > 1 ? 's' : ''} · ${fmtMoney(addonExtra)}/mo on top of the plan`
                : 'No add-ons on this account.'}
          </div>
        </>
      );
    }

    if (f.t === 'area') {
      return (
        <textarea
          value={value}
          placeholder="What was agreed, what to do next…"
          onChange={e => set(f.k, e.target.value)}
        />
      );
    }

    return (
      <input
        value={value}
        placeholder={f.hint}
        autoFocus={f.k === 'name'}
        onChange={e => set(f.k, e.target.value)}
      />
    );
  };

  /** The shortcut links under certain fields. */
  const quickFor = key => {
    if (key === 'trialEnd') {
      return (
        <QuickActions>
          <button type="button" onClick={setTrialFromRegistration}>Registration + {S.trialDays} days</button>
          <button type="button" onClick={() => set('trialEnd', NO_TRIAL)}>No trial</button>
        </QuickActions>
      );
    }
    if (key === 'amount') {
      return <QuickActions><button type="button" onClick={usePlanPrice}>Use plan price</button></QuickActions>;
    }
    if (key === 'nextDue') {
      return (
        <QuickActions>
          <button
            type="button"
            onClick={() => set('nextDue', formatDate(addPeriod(new Date(), form.cycle || 'Monthly'), false, S.dateFmt))}
          >
            One period from today
          </button>
          <button
            type="button"
            onClick={() => {
              if (!id) { toast('Save the client first'); return; }
              onClose();
              markPaid([id]);
            }}
          >
            Mark paid now
          </button>
        </QuickActions>
      );
    }
    if (key === 'lastActivity') {
      return (
        <QuickActions>
          <button type="button" onClick={() => set('lastActivity', formatDate(new Date(), false, S.dateFmt))}>
            Set to today
          </button>
        </QuickActions>
      );
    }
    if (key === 'lastContacted') {
      return (
        <QuickActions>
          <button type="button" onClick={() => set('lastContacted', formatDate(new Date(), false, S.dateFmt))}>
            Set to today
          </button>
        </QuickActions>
      );
    }
    return null;
  };

  let lastGroup = '';

  return (
    <Sheet
      icon={id ? 'edit' : 'plus'}
      title={id ? 'Edit client' : 'Add client'}
      onClose={onClose}
      onSave={save}
      saveLabel="Save client"
      footerLeft={id ? (
        <button
          type="button"
          className="btn"
          onClick={() => { onClose(); openDraft({ kind: 'client', id }); }}
        >
          Draft email
        </button>
      ) : null}
    >
      {EDITABLE.map(f => {
        const heading = f.g !== lastGroup ? f.g : null;
        lastGroup = f.g;
        return (
          <div key={f.k} style={{ display: 'contents' }}>
            {heading ? <Group>{heading}</Group> : null}
            <Field label={headingOf(f, S)} wide={f.wide} hint={quickFor(f.k)}>
              {renderInput(f)}
            </Field>
          </div>
        );
      })}
      {id && !isOffline() ? (
        <div style={{ display: 'contents' }}>
          <Group>Statement</Group>
          <ClientStatement id={id} />
        </div>
      ) : null}
      <Warning messages={warnings} />
    </Sheet>
  );
}
